import { formatMoney, type Centavos } from '@abastece/shared';
import type { SesionCaja } from '@abastece/core';
import { pasosDeLosDolares, pasosDelEsperado } from '../lib/caja.js';
import { localeActual, useT } from '../i18n/index.js';
import { Interruptor } from './Campo.js';
import { ConMarca, Explicacion, type PasoDelCalculo } from './Explicacion.js';

/**
 * What the till's counts found, said the same way wherever they are said: the till's screen, and
 * the steps of «Empezar el día», «Terminar mi turno» and «Recibir la caja».
 */

/** Dollars as the drawer holds them: «US$ 20.00», and «US$ -5.00» for a difference. */
export const enDolares = (c: Centavos, signed = false) => `US$ ${formatMoney(c, { symbol: false, signed })}`;

/** A Nicaraguan date as the steps say it: «3 oct.», «Oct 3». */
export const fechaCorta = (fecha: string) =>
  new Date(`${fecha}T12:00:00Z`).toLocaleDateString(localeActual(), { day: 'numeric', month: 'short', timeZone: 'UTC' });

export function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="min-w-0 text-sm text-slate-400">{etiqueta}</span>
      <span className="tabular shrink-0 text-sm font-medium whitespace-nowrap">{valor}</span>
    </div>
  );
}

/**
 * A line whose value is words rather than an amount: under its label and wrapping, so that a long
 * one («En efectivo, del efectivo general», a client's full name) never runs into it on a phone.
 */
export function DatoTexto({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm text-slate-400">{etiqueta}</span>
      <span className="text-sm font-medium wrap-break-word">{valor}</span>
    </div>
  );
}

/** What a drawer was expected to hold, explained: what that is, and its sum, a line each. */
export interface CalculoDelEsperado {
  texto: string;
  pasos: readonly PasoDelCalculo[];
}

/**
 * «Esperado en córdobas», or in dollars, worked out from the drawer as it stood when it was counted
 * (`pasosDelEsperado`): what a shortage or an overage is measured against, and what made it. Said
 * once the count is in, so a blind count stays blind.
 */
export function calculoDelEsperado(s: SesionCaja, moneda: 'NIO' | 'USD', t: ReturnType<typeof useT>): CalculoDelEsperado {
  const formato = moneda === 'USD' ? (c: Centavos) => enDolares(c) : (c: Centavos) => formatMoney(c);
  return {
    texto: t(moneda === 'USD' ? 'caja.esperadoDolaresAyuda' : 'caja.esperadoAyuda'),
    pasos: (moneda === 'USD' ? pasosDeLosDolares(s) : pasosDelEsperado(s)).map((p) => ({
      ...(p.signo ? { signo: p.signo } : {}),
      etiqueta: t(p.clave),
      monto: formato(p.monto),
    })),
  };
}

/** A `Dato` that is a button for its bubble, as wide as the line, lit up under the mouse. */
const FILA = '-mx-2 flex w-[calc(100%+1rem)] items-baseline justify-between gap-4 rounded-md px-2 hover:bg-white/5';

/** One currency's count: what was expected, what was counted, and whether it matched. */
export function Veredicto({
  esperado,
  valorEsperado,
  valorContado,
  diferencia,
  cuadra,
  formato,
  primerConteo,
  calculo,
}: {
  esperado: string;
  valorEsperado: string;
  valorContado: string;
  diferencia: Centavos;
  cuadra: string;
  formato: (c: Centavos) => string;
  /** The first count, when it was off and the drawer was counted again. */
  primerConteo?: string | null;
  /** What the expected figure is made of, in a bubble on its line, with an ⓘ to say so. */
  calculo?: CalculoDelEsperado;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      {calculo ? (
        <Explicacion texto={calculo.texto} titulo={esperado} calculo={calculo.pasos} className={FILA}>
          <span className="min-w-0 text-sm text-slate-400 transition group-hover/explicacion:text-slate-200">
            <ConMarca>{esperado}</ConMarca>
          </span>
          <span className="tabular shrink-0 text-sm font-medium whitespace-nowrap">{valorEsperado}</span>
        </Explicacion>
      ) : (
        <Dato etiqueta={esperado} valor={valorEsperado} />
      )}
      {primerConteo && <Dato etiqueta={t('turno.primerConteo')} valor={primerConteo} />}
      <Dato etiqueta={t(primerConteo ? 'turno.segundoConteo' : 'caja.contado')} valor={valorContado} />
      <div
        className={`rounded-xl px-4 py-3 ${
          diferencia === 0 ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-200'
        }`}
      >
        <p className="text-sm font-medium">
          {diferencia === 0 ? cuadra : t(diferencia > 0 ? 'caja.sobrante' : 'caja.faltante')}
        </p>
        {diferencia !== 0 && (
          <p className="tabular mt-1 text-2xl font-bold">{formato(Math.abs(diferencia) as Centavos)}</p>
        )}
      </div>
    </div>
  );
}

/** What a corte found, in both currencies, and what it took out of the drawer. */
export interface Corte {
  esperadoC: Centavos;
  contadoC: Centavos;
  esperadoD: Centavos;
  contadoD: Centavos;
  saque: { queda: Centavos; cordobas: Centavos; dolares: Centavos; recibido: boolean };
  primerConteoC?: Centavos | null;
  primerConteoD?: Centavos | null;
}

/**
 * What the corte found. Dollars get their own verdict whenever there were any, expected or
 * counted: one «cuadra» over both hid which currency was off. Given the drawer as it stood when it
 * was counted, each expected figure says what it is made of.
 */
export function CuentasDelCorte({ corte, caja }: { corte: Corte; caja?: SesionCaja }) {
  const t = useT();
  const conDolares = corte.esperadoD > 0 || corte.contadoD > 0;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-5" data-guia="caja-diferencia">
        <Veredicto
          esperado={t('caja.esperadoCordobas')}
          valorEsperado={formatMoney(corte.esperadoC)}
          valorContado={formatMoney(corte.contadoC)}
          diferencia={(corte.contadoC - corte.esperadoC) as Centavos}
          cuadra={t(conDolares ? 'caja.cordobasCuadran' : 'caja.cuadraExacto')}
          formato={(c) => formatMoney(c)}
          primerConteo={corte.primerConteoC != null ? formatMoney(corte.primerConteoC) : null}
          {...(caja ? { calculo: calculoDelEsperado(caja, 'NIO', t) } : {})}
        />
        {conDolares && (
          <Veredicto
            esperado={t('caja.esperadoDolares')}
            valorEsperado={enDolares(corte.esperadoD)}
            valorContado={enDolares(corte.contadoD)}
            diferencia={(corte.contadoD - corte.esperadoD) as Centavos}
            cuadra={t('caja.dolaresCuadran')}
            formato={(c) => enDolares(c)}
            primerConteo={corte.primerConteoD != null ? enDolares(corte.primerConteoD) : null}
            {...(caja ? { calculo: calculoDelEsperado(caja, 'USD', t) } : {})}
          />
        )}
      </div>
      <Saque saque={corte.saque} />
    </div>
  );
}

/**
 * The day's money, out of the drawer: what to take to general cash, and what stays for change.
 * Said as amounts to pick up, because that is what whoever closes does next.
 */
export function Saque({ saque }: { saque: Corte['saque'] }) {
  const t = useT();
  const nada = saque.cordobas === 0 && saque.dolares === 0;
  return (
    <div data-guia="caja-saque" className="rounded-xl bg-slate-800/60 px-4 py-3">
      {nada ? (
        <p className="text-sm">
          {saque.queda > 0
            ? t('caja.nadaQueSacar', { monto: formatMoney(saque.queda) })
            : t('caja.gavetaVacia')}
        </p>
      ) : (
        <>
          <p className="text-sm font-medium text-slate-300">{t('caja.saque')}</p>
          <p className="tabular mt-1 text-2xl font-bold">{formatMoney(saque.cordobas)}</p>
          {saque.dolares > 0 && (
            <p className="tabular text-lg font-semibold">+ {enDolares(saque.dolares)}</p>
          )}
          {/* Whoever took it in hand has it in general cash; nobody did, and it goes to the owner. */}
          <p className={`mt-2 text-xs ${saque.recibido ? 'text-slate-400' : 'text-amber-300'}`}>
            {saque.queda > 0
              ? t(saque.recibido ? 'caja.saqueNota' : 'caja.saqueNotaPorRecibir', { monto: formatMoney(saque.queda) })
              : t(saque.recibido ? 'caja.saqueTodo' : 'caja.saqueTodoPorRecibir')}
          </p>
        </>
      )}
    </div>
  );
}

/** An opening count against what the last corte left, with what was put in from general cash. */
export interface Apertura {
  quedo: Centavos;
  puesto: Centavos;
  contado: Centavos;
  primerConteo?: Centavos | null;
}

/**
 * What opening found. Money gone from the drawer overnight is said the morning after, while
 * everybody still remembers who closed, instead of turning up weeks later as nobody's shortfall.
 * A drawer the last corte left nothing in is held against what was put in for change alone
 * (2026-10-14), and says nothing of a corte.
 */
export function CuentasDeApertura({ apertura }: { apertura: Apertura }) {
  const t = useT();
  const esperado = (apertura.quedo + apertura.puesto) as Centavos;
  const diferencia = (apertura.contado - esperado) as Centavos;
  const sinFondo = apertura.quedo === 0;
  return (
    <div className="flex flex-col gap-2">
      {apertura.puesto > 0 && (
        <>
          {!sinFondo && <Dato etiqueta={t('caja.dejoElCorte')} valor={formatMoney(apertura.quedo)} />}
          <Dato etiqueta={t('caja.puestoDeGeneral')} valor={formatMoney(apertura.puesto)} />
        </>
      )}
      <Veredicto
        esperado={t(apertura.puesto > 0 || sinFondo ? 'caja.debiaHaber' : 'caja.dejoElCorte')}
        valorEsperado={formatMoney(esperado)}
        valorContado={formatMoney(apertura.contado)}
        diferencia={diferencia}
        cuadra={t(
          sinFondo
            ? apertura.puesto > 0
              ? 'caja.aperturaCuadraLoPuesto'
              : 'caja.aperturaCuadraVacia'
            : apertura.puesto > 0
              ? 'caja.aperturaCuadraPuesto'
              : 'caja.aperturaCuadra',
        )}
        formato={(c) => formatMoney(c)}
        primerConteo={apertura.primerConteo != null ? formatMoney(apertura.primerConteo) : null}
      />
      {diferencia !== 0 && <p className="mt-2 text-xs text-slate-500">{t('caja.aperturaExplicacion')}</p>}
    </div>
  );
}

/**
 * Who takes the money in hand as it leaves the drawer. The owner, there and then — their PIN —
 * and it goes straight to general cash; otherwise it waits to be received, and the owner counts
 * it when they get it, against what the drawer handed over.
 */
export function QuienRecibe({
  puedeRecibir,
  ahora,
  setAhora,
}: {
  puedeRecibir: boolean;
  ahora: boolean;
  setAhora: (v: boolean) => void;
}) {
  const t = useT();
  if (puedeRecibir) return <p className="text-xs text-slate-400">{t('caja.ustedRecibe')}</p>;
  return (
    <div className="rounded-xl bg-slate-800/60 px-3">
      <Interruptor
        etiqueta={t('caja.recibeAhora')}
        descripcion={t(ahora ? 'caja.recibeAhoraAyuda' : 'caja.porRecibirAyuda')}
        checked={ahora}
        onChange={setAhora}
      />
    </div>
  );
}
